import './box-summary.css'
import { getDynamicBundleItems } from '@rechargeapps/storefront-client'
import BoxState, { EVENT_TYPES } from '../../scripts/lib/box-state.js'
import CustomerService from '../../scripts/lib/customer-service.js'
import { getFeedingAmount } from '../../scripts/lib/recommendation.js'
import { shouldAddGwpToCart, buildGwpCartItem, productQualifiesForGwp } from '../../scripts/lib/gwp-helpers.js'
import { transformPetsForRecommendation } from '../../scripts/lib/pet-profile.js'
import { renderPetRecommendationHtml } from '../../scripts/lib/pet-display-helpers.js'
import { DEFAULT_MEAL_PLAN_URL, MIN_MANUAL_BOX_BUILDER_WEIGHT_LBS } from '../../scripts/lib/constants.js'
import { buildBundleSelections, buildCartItems, updateThawingContainerVisibility } from '../../scripts/lib/bundle-helpers.js'
import { getSellingPlanForItem } from '../../scripts/lib/selling-plan-helpers.js'

const SELECTORS = {
  TOTAL_WEIGHT: '[data-total-weight-el]',
  PRICE_PER_DAY: '[data-price-per-day-el]',
  FREQUENCY: '[data-frequency-el]',
  REVIEW_BUTTON: '[data-review-button]',
  EDIT_SUBSCRIPTION_BUTTON: '[data-edit-subscription-button]',
  FREQUENCY_TEXT: '[data-frequency-text]',
  BUTTON_MAIN_TEXT: '[data-button-main-text]',
  PRICE_TEXT: '[data-price-text]',
  ONE_TIME_LINK: '[data-one-time-link]'
}

class BoxSummary extends HTMLElement {
  #boxItems = []
  #recipeCardElements = new Map()
  #groupSubtotalElements = new Map()
  #recipeGroupsContainer = null
  #boxState
  #weightEl
  #pricePerDayEl
  #frequencyEl
  #reviewButtonEl
  #editSubscriptionButtonEl
  #frequencyTextEl
  #buttonMainTextEl
  #priceTextEl
  #oneTimeLinkEl
  #isCheckoutInProgress = false
  #isSyncInProgress = false
  #hasExternalDiscounts = false
  #boundHandleFrequencyChange
  #boundHandlePetProfilesChange
  #boundHandleEligibilityChange
  #boundHandleCartDiscountCodesChange
  #boundCartSyncHandler
  #boundCartSyncStartHandler
  #boundCartSyncErrorHandler

  constructor() {
    super()
    this.#boxState = BoxState.getInstance()
    this.#boundHandleFrequencyChange = () => this.#handleFrequencyChange()
    this.#boundHandlePetProfilesChange = () => this.#renderPetRecommendations()
    this.#boundHandleEligibilityChange = () => this.#updateSubscriptionButtons()
    this.#boundHandleCartDiscountCodesChange = () => this.#render()
    this.#boundCartSyncHandler = (e) => this.#updatePriceFromCartState(e.detail)
    this.#boundCartSyncStartHandler = () => this.#handleCartSyncStart()
    this.#boundCartSyncErrorHandler = () => this.#handleCartSyncError()
  }

  get boxItems() {
    return this.#boxItems
  }

  set boxItems(items) {
    const hadItems = this.#boxItems.length > 0
    this.#boxItems = items

    if (hadItems && !this.#isCheckoutInProgress && this.#hasExternalDiscounts) {
      this.#isSyncInProgress = true
    }

    this.#render()
  }

  get #totalQuantity() {
    return this.#boxItems.reduce((sum, item) => sum + item.quantity, 0)
  }

  get #totalWeight() {
    return this.#totalQuantity * 2
  }

  get #subtotal() {
    return this.#boxItems.reduce((sum, item) => sum + (item.price * item.quantity), 0)
  }

  get #shippingFrequency() {
    return parseInt(this.#frequencyEl?.value || '4', 10)
  }

  get #pricePerDay() {
    const daysInPeriod = this.#shippingFrequency * 7
    return this.#subtotal / daysInPeriod
  }

  get #oneTimeCartItems() {
    const items = this.#boxItems
      .filter(item => item.quantity > 0)
      .map(item => ({ id: parseInt(item.variantId, 10), quantity: item.quantity }))

    if (shouldAddGwpToCart(this.#boxItems)) {
      items.push(buildGwpCartItem())
    }

    return items
  }

  get #formattedPrice() {
    const priceInDollars = this.#subtotal / 100
    return `$${priceInDollars.toFixed(2)}`
  }

  #getGroupInfo(item) {
    const collection = item.collections[0]

    return {
      label: collection.groupLabel || collection.title,
      sortOrder: collection.sortOrder ?? 0,
      handle: collection.handle
    }
  }

  #groupAndSortItems(items) {
    const groupsMap = new Map()

    items.forEach(item => {
      const { label, sortOrder, handle } = this.#getGroupInfo(item)
      if (!groupsMap.has(handle)) {
        groupsMap.set(handle, { label, items: [], sortOrder })
      }
      groupsMap.get(handle).items.push(item)
    })

    return Array.from(groupsMap.entries())
      .sort((a, b) => a[1].sortOrder - b[1].sortOrder)
  }

  #calculateGroupSubtotal(items) {
    return items.reduce((sum, item) => sum + (item.quantity * 2), 0)
  }

  #createGroupElement(groupLabel, groupHandle) {
    const wrapper = document.createElement('div')
    wrapper.className = 'v:flex v:flex-col v:gap-4'
    wrapper.dataset.groupWrapper = groupHandle

    wrapper.innerHTML = `
      <div class="v:flex v:items-center v:justify-between">
        <p class="v:label-md v:font-bold v:text-brand-brown">${groupLabel}</p>
        <p class="v:label-md v:font-bold v:text-brand-brown" data-group-subtotal>
          Subtotal <span class="v:text-brand-orange-dark" data-subtotal="${groupHandle}">0 lbs</span>
        </p>
      </div>
      <div class="v:flex v:flex-col v:gap-4" data-group="${groupHandle}"></div>
    `

    const subtotalContainer = wrapper.querySelector('[data-group-subtotal]')
    this.#groupSubtotalElements.set(groupHandle, subtotalContainer)

    return wrapper
  }

  #createRecipeCard(item) {
    const card = document.createElement('recipe-card')
    card.classList.add('recipe-card')

    Object.entries(item).forEach(([key, value]) => {
      card.dataset[key] = value || ''
    })

    card.dataset.layout = 'horizontal'
    card.dataset.showActions = 'true'
    card.dataset.showSpecialBadge = 'false'
    card.dataset.state = item.quantity > 0 ? 'active' : 'idle'

    return card
  }

  #updateRecipeCard(card, item) {
    const currentQuantity = parseInt(card.dataset.quantity || '0', 10)
    const newQuantity = parseInt(item.quantity || 0, 10)

    if (currentQuantity !== newQuantity) {
      card.dataset.quantity = newQuantity
    }

    const newState = newQuantity > 0 ? 'active' : 'idle'
    if (card.dataset.state !== newState) {
      card.dataset.state = newState
    }
  }

  #loadPetProfiles() {
    const pets = this.#boxState.petProfiles
    if (!Array.isArray(pets) || pets.length === 0) return []

    const transformedPets = transformPetsForRecommendation(pets)
    return transformedPets.map(pet => ({
      name: pet.name,
      species: pet.species,
      dailyOz: getFeedingAmount(pet)
    }))
  }

  async #prepareSubscriptionCheckout() {
    await this.#boxState.waitForCartSync()

    const boxConfig = this.#boxState.boxConfig

    if (!boxConfig?.bundle?.productId || !boxConfig?.bundle?.variantId) {
      this.#isCheckoutInProgress = false
      this.#reviewButtonEl.disabled = false
      this.#restoreButtonText()
      return
    }

    try {
      const frequency = this.#shippingFrequency
      const itemsWithQuantity = this.#boxItems.filter(i => i.quantity > 0)
      const sellingPlanMap = Object.fromEntries(
        itemsWithQuantity.map(item => [item.productId, getSellingPlanForItem(item.productId, frequency, window.all_products)])
      )

      const missingSellingPlans = itemsWithQuantity.filter(item => !sellingPlanMap[item.productId])
      if (missingSellingPlans.length > 0) {
        throw new Error(`Missing selling plan for ${missingSellingPlans[0].productTitle}`)
      }

      const bundleSelections = buildBundleSelections({
        items: this.#boxItems,
        sellingPlanMap
      })

      const bundle = {
        externalProductId: boxConfig.bundle.productId,
        externalVariantId: boxConfig.bundle.variantId,
        selections: bundleSelections
      }

      const bundleItems = await getDynamicBundleItems(bundle, boxConfig.bundle.handle || '')

      const { rootPath } = boxConfig

      await fetch(rootPath + 'cart/clear.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })

      const cartItems = await buildCartItems({
        bundleItems,
        thawingContainerVariantId: boxConfig.subscription.thawingContainerVariantId,
        gwpItem: shouldAddGwpToCart(this.#boxItems) ? buildGwpCartItem() : null
      })

      await fetch(rootPath + 'cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ items: cartItems })
      })

      window.selected_frequency = this.#shippingFrequency

      this.#boxState.markCheckoutStarted()

      window.location.href = boxConfig.rootPath + 'checkout'

    } catch (error) {
      console.error('Failed to prepare subscription checkout:', error)
      this.#isCheckoutInProgress = false
      this.#reviewButtonEl.disabled = false
      this.#restoreButtonText()
    }
  }

  async #prepareOneTimeCheckout() {
    try {
      this.#boxState.cancelPendingCartSync()

      const rootPath = this.#boxState.boxConfig?.rootPath || '/'
      const headers = {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      }

      const clearResponse = await fetch(rootPath + 'cart/clear.js', { method: 'POST', headers })
      if (!clearResponse.ok) throw new Error(`cart/clear.js failed: ${clearResponse.status}`)

      const updates = {}
      for (const item of this.#oneTimeCartItems) {
        updates[item.id] = item.quantity
      }

      const updateResponse = await fetch(rootPath + 'cart/update.js', {
        method: 'POST',
        headers,
        body: JSON.stringify({ updates, discount: '' })
      })
      if (!updateResponse.ok) throw new Error(`cart/update.js failed: ${updateResponse.status}`)

      window.location.href = rootPath + 'checkout?discount='
    } catch (error) {
      console.error('Failed to prepare one-time checkout:', error)
      this.#oneTimeLinkEl.classList.remove('v:opacity-50', 'v:pointer-events-none')
      this.#render()
    }
  }

  connectedCallback() {
    if (this.hasOwnProperty('boxItems')) {
      const value = this.boxItems
      delete this.boxItems
      this.boxItems = value
    }

    this.#recipeGroupsContainer = this.querySelector('[data-recipe-groups-container]')
    this.#weightEl = this.querySelector(SELECTORS.TOTAL_WEIGHT)
    this.#pricePerDayEl = this.querySelector(SELECTORS.PRICE_PER_DAY)
    this.#frequencyEl = this.querySelector(SELECTORS.FREQUENCY)
    this.#reviewButtonEl = this.querySelector(SELECTORS.REVIEW_BUTTON)
    this.#editSubscriptionButtonEl = this.querySelector(SELECTORS.EDIT_SUBSCRIPTION_BUTTON)
    this.#frequencyTextEl = this.querySelector(SELECTORS.FREQUENCY_TEXT)
    this.#buttonMainTextEl = this.querySelector(SELECTORS.BUTTON_MAIN_TEXT)
    this.#priceTextEl = this.querySelector(SELECTORS.PRICE_TEXT)
    this.#oneTimeLinkEl = this.querySelector(SELECTORS.ONE_TIME_LINK)

    const savedFrequency = this.#boxState.frequency
    if (savedFrequency && this.#frequencyEl) {
      this.#frequencyEl.value = savedFrequency.toString()
    }

    this.#frequencyEl?.addEventListener('change', this.#boundHandleFrequencyChange)
    this.#boxState.subscribe(EVENT_TYPES.PET_PROFILES_CHANGE, this.#boundHandlePetProfilesChange)
    this.#boxState.subscribe(EVENT_TYPES.ELIGIBILITY_CHANGE, this.#boundHandleEligibilityChange)
    this.#boxState.subscribe(EVENT_TYPES.CART_DISCOUNT_CODES_CHANGE, this.#boundHandleCartDiscountCodesChange)
    window.addEventListener('cart-sync:start', this.#boundCartSyncStartHandler)
    window.addEventListener('cart-sync:complete', this.#boundCartSyncHandler)
    window.addEventListener('cart-sync:error', this.#boundCartSyncErrorHandler)

    this.#hasExternalDiscounts = window.VIVA?.hasExternalDiscounts ?? false

    if (this.#hasExternalDiscounts && this.#buttonMainTextEl && this.#reviewButtonEl) {
      this.#isSyncInProgress = true
      this.#buttonMainTextEl.textContent = 'Calculating...'
      this.#reviewButtonEl.disabled = true
    }

    this.#updateSubscriptionButtons()

    this.#reviewButtonEl?.addEventListener('click', async (e) => {
      e.preventDefault()
      if (!this.#reviewButtonEl.disabled) {
        const hasMinimumWeight = this.#totalWeight >= MIN_MANUAL_BOX_BUILDER_WEIGHT_LBS
        if (hasMinimumWeight) {
          this.#isCheckoutInProgress = true
          this.#reviewButtonEl.disabled = true
          this.#buttonMainTextEl.textContent = 'Processing...'

          CustomerService.getInstance().clearEligibilityCache()

          await this.#prepareSubscriptionCheckout()
        }
      }
    })

    this.#oneTimeLinkEl?.addEventListener('click', async (e) => {
      if (this.#oneTimeCartItems.length === 0) return

      e.preventDefault()
      this.#oneTimeLinkEl.classList.add('v:opacity-50', 'v:pointer-events-none')
      await this.#prepareOneTimeCheckout()
    })

    this.#render()
    this.hidden = false

    updateThawingContainerVisibility(this, this.#boxState.boxConfig?.subscription?.thawingContainerVariantId)
  }

  disconnectedCallback() {
    this.#frequencyEl?.removeEventListener('change', this.#boundHandleFrequencyChange)
    this.#boxState.unsubscribe(EVENT_TYPES.PET_PROFILES_CHANGE, this.#boundHandlePetProfilesChange)
    this.#boxState.unsubscribe(EVENT_TYPES.ELIGIBILITY_CHANGE, this.#boundHandleEligibilityChange)
    this.#boxState.unsubscribe(EVENT_TYPES.CART_DISCOUNT_CODES_CHANGE, this.#boundHandleCartDiscountCodesChange)
    window.removeEventListener('cart-sync:start', this.#boundCartSyncStartHandler)
    window.removeEventListener('cart-sync:complete', this.#boundCartSyncHandler)
    window.removeEventListener('cart-sync:error', this.#boundCartSyncErrorHandler)
  }

  #handleFrequencyChange() {
    this.#render()
    
    const frequency = this.#shippingFrequency
    this.dispatchEvent(new CustomEvent('box-summary:frequency-change', {
      detail: { frequency },
      bubbles: true
    }))
  }

  #updateSubscriptionButtons() {
    const canSubscribe = this.#boxState.canSubscribe

    if (this.#editSubscriptionButtonEl) {
      this.#editSubscriptionButtonEl.hidden = canSubscribe
    }

    if (this.#reviewButtonEl) {
      this.#reviewButtonEl.hidden = !canSubscribe
    }
  }

  #updatePriceFromCartState(cartState) {
    if (this.#isCheckoutInProgress) return
    if (cartState.hasMorePending) return

    this.#isSyncInProgress = false

    if (!this.#hasExternalDiscounts) return

    const { totalPrice } = cartState
    const hasMinimumWeight = this.#totalWeight >= MIN_MANUAL_BOX_BUILDER_WEIGHT_LBS

    if (this.#buttonMainTextEl && hasMinimumWeight) {
      const priceInDollars = totalPrice / 100
      this.#buttonMainTextEl.innerHTML = `Subscribe for <span data-price-text>$${priceInDollars.toFixed(2)}</span>`
      this.#priceTextEl = this.querySelector('[data-price-text]')
    }

    if (this.#reviewButtonEl) {
      this.#reviewButtonEl.disabled = !hasMinimumWeight
    }
  }

  #handleCartSyncStart() {
    if (this.#isCheckoutInProgress) return
    if (!this.#hasExternalDiscounts) return

    this.#isSyncInProgress = true
    this.#render()
  }

  #handleCartSyncError() {
    if (this.#isCheckoutInProgress) return

    this.#isSyncInProgress = false
    const hasMinimumWeight = this.#totalWeight >= MIN_MANUAL_BOX_BUILDER_WEIGHT_LBS

    this.#restoreButtonText()

    if (this.#reviewButtonEl) {
      this.#reviewButtonEl.disabled = !hasMinimumWeight
    }
  }

  #restoreButtonText() {
    const hasMinimumWeight = this.#totalWeight >= MIN_MANUAL_BOX_BUILDER_WEIGHT_LBS
    if (this.#buttonMainTextEl && hasMinimumWeight) {
      this.#buttonMainTextEl.innerHTML = `Subscribe for <span data-price-text>${this.#formattedPrice}</span>`
      this.#priceTextEl = this.querySelector('[data-price-text]')
    }
  }

  #render() {
    const totalWeight = this.#totalWeight
    const hasMinimumWeight = totalWeight >= MIN_MANUAL_BOX_BUILDER_WEIGHT_LBS
    const frequency = this.#shippingFrequency
    const subtotal = this.#subtotal

    this.setAttribute('data-has-minimum-weight', hasMinimumWeight.toString())

    if (this.#weightEl) {
      this.#weightEl.textContent = `${totalWeight} lbs`
    }

    if (this.#pricePerDayEl) {
      const priceInDollars = this.#pricePerDay / 100
      this.#pricePerDayEl.textContent = priceInDollars === 0
        ? '$-/day'
        : `$${priceInDollars.toFixed(2)}/day`
    }

    if (this.#frequencyTextEl) {
      this.#frequencyTextEl.textContent = `${frequency} weeks`
    }

    if (this.#reviewButtonEl) {
      const syncBlocking = this.#hasExternalDiscounts && this.#isSyncInProgress
      this.#reviewButtonEl.disabled = !hasMinimumWeight || !this.#boxState.cartDiscountCodesFetched || syncBlocking
    }

    if (this.#buttonMainTextEl) {
      const showProcessingText = this.#hasExternalDiscounts && this.#isSyncInProgress && hasMinimumWeight
      if (showProcessingText) {
        this.#buttonMainTextEl.textContent = 'Processing...'
      } else if (this.#priceTextEl) {
        const priceInDollars = subtotal / 100
        this.#priceTextEl.textContent = hasMinimumWeight
          ? `$${priceInDollars.toFixed(2)}`
          : ''
      }
    }

    if (this.#oneTimeLinkEl) {
      this.#oneTimeLinkEl.hidden = false
      this.#oneTimeLinkEl.href = '#'
      this.#oneTimeLinkEl.classList.toggle('v:opacity-50', !hasMinimumWeight)
      this.#oneTimeLinkEl.classList.toggle('v:pointer-events-none', !hasMinimumWeight)

      if (hasMinimumWeight) {
        const priceInDollars = subtotal / 100
        this.#oneTimeLinkEl.innerHTML = `Buy one-time box for <span data-one-time-price>$${priceInDollars.toFixed(2)}</span> + shipping`
      } else {
        this.#oneTimeLinkEl.innerHTML = `Buy one-time box`
      }
    }

    this.#renderRecipeCards()
    this.#renderPetRecommendations()
    this.#renderGiftSection()
  }

  #renderPetRecommendations() {
    const container = this.querySelector('[data-pet-recommendations-container]')
    if (!container) return

    const defaultMessage = this.querySelector('[data-default-recommendation]')
    const petList = this.querySelector('[data-pet-list]')
    const addPetLink = this.querySelector('[data-add-pet-link]')
    const shippingTextPrefix = this.querySelector('[data-shipping-text-prefix]')
    const shippingTextSuffix = this.querySelector('[data-shipping-text-suffix]')

    const allPets = this.#loadPetProfiles()

    const seen = new Set()
    const petProfiles = allPets.filter(pet => {
      const key = `${pet.name}-${pet.species}-${pet.dailyOz}`
      if (seen.has(key)) {
        return false
      }
      seen.add(key)
      return true
    })

    if (petProfiles.length === 0) {
      if (defaultMessage) defaultMessage.hidden = false
      if (petList) petList.hidden = true
      if (addPetLink) addPetLink.hidden = true
      if (shippingTextPrefix) shippingTextPrefix.textContent = "We'll ship"
      if (shippingTextSuffix) shippingTextSuffix.textContent = ""
    } else {
      if (defaultMessage) defaultMessage.hidden = true
      if (petList) {
        petList.hidden = false

        const frequency = this.#shippingFrequency
        const mealPlanUrl = this.dataset.mealPlanUrl || DEFAULT_MEAL_PLAN_URL

        petList.innerHTML = petProfiles.map(pet => 
          renderPetRecommendationHtml(pet, frequency, mealPlanUrl)
        ).join('')
      }

      if (addPetLink) addPetLink.hidden = false
      if (shippingTextPrefix) shippingTextPrefix.textContent = "For"
      if (shippingTextSuffix) shippingTextSuffix.textContent = "we recommend"
    }
  }

  #renderGiftSection() {
    const container = this.querySelector('[data-gift-section]')
    if (!container) return

    const qualifyingItems = this.#boxItems.filter(item =>
      item.quantity > 0 && productQualifiesForGwp(item.productTitle)
    )

    container.hidden = qualifyingItems.length === 0

    if (qualifyingItems.length > 0) {
      const descriptionEl = container.querySelector('[data-gift-description]')
      if (descriptionEl) {
        if (qualifyingItems.length === 1) {
          descriptionEl.textContent = `Free with your purchase of ${qualifyingItems[0].productTitle}!`
        } else {
          descriptionEl.textContent = 'Free with your qualifying special purchase!'
        }
      }
    }
  }

  #renderRecipeCards() {
    const container = this.#recipeGroupsContainer
    if (!container) return

    const sortedGroups = this.#groupAndSortItems(this.#boxItems)

    const activeGroupHandles = new Set()

    sortedGroups.forEach(([groupHandle, { label, items }], index) => {
      activeGroupHandles.add(groupHandle)

      let groupWrapper = container.querySelector(`[data-group-wrapper="${groupHandle}"]`)

      if (!groupWrapper) {
        groupWrapper = this.#createGroupElement(label, groupHandle)

        const existingGroups = container.querySelectorAll('[data-group-wrapper]')
        if (index < existingGroups.length) {
          container.insertBefore(groupWrapper, existingGroups[index])
        } else {
          container.appendChild(groupWrapper)
        }
      }

      const subtotal = this.#calculateGroupSubtotal(items)
      const subtotalEl = groupWrapper.querySelector(`[data-subtotal="${groupHandle}"]`)
      if (subtotalEl && subtotalEl.textContent !== `${subtotal} lbs`) {
        subtotalEl.textContent = `${subtotal} lbs`
      }

      const subtotalContainer = this.#groupSubtotalElements.get(groupHandle)
      if (subtotalContainer) {
        subtotalContainer.hidden = sortedGroups.length <= 1
      }

      const groupContainer = groupWrapper.querySelector(`[data-group="${groupHandle}"]`)
      items.forEach(item => {
        let card = this.#recipeCardElements.get(item.variantId)

        if (!card) {
          card = this.#createRecipeCard(item)
          this.#recipeCardElements.set(item.variantId, card)
        } else {
          this.#updateRecipeCard(card, item)
        }

        if (card.parentElement !== groupContainer) {
          groupContainer.appendChild(card)
        }
      })
    })

    container.querySelectorAll('[data-group-wrapper]').forEach(groupWrapper => {
      const groupHandle = groupWrapper.dataset.groupWrapper
      if (!activeGroupHandles.has(groupHandle)) {
        groupWrapper.remove()
        this.#groupSubtotalElements.delete(groupHandle)
      }
    })

    const currentVariantIds = new Set(this.#boxItems.map(item => item.variantId))
    this.#recipeCardElements.forEach((card, variantId) => {
      if (!currentVariantIds.has(variantId)) {
        card.remove()
        this.#recipeCardElements.delete(variantId)
      }
    })
  }
}

if (!customElements.get('box-summary')) {
  customElements.define('box-summary', BoxSummary)
}

export default BoxSummary